import React from "react"
import PropTypes from "prop-types"
import { Row, Col, Card } from "antd"
import { useTranslation } from "react-i18next"
import IconCard from "pagesV2/pro/Experiences/ListingPreview/IconCard"
import { useItemMapping } from "../../hooks/useItemMapping"
import styles from "../../index.module.css"

const TripDetailsCard = React.memo(
  ({ includedItems, bringItems, skillLevels, alcoholAllowed, accessibilityEnabled, childrenAllowed }) => {
    const { t } = useTranslation()
    const { getItemDetails, getSkillLevelDetails, getOtherDetails, getNotAllowedDetails } = useItemMapping()

    const renderCardSection = (title, items) => (
      <div className={styles.sectionSpacing}>
        <h2 className={`${styles.subTitle} pb-m`}>
          {title}
        </h2>
        <Row gutter={[8, 8]}>
          {items.map((item) => {
            const { label, icon } = getItemDetails(item)
            return (
              <Col xs={24} sm={12} key={item}>
                <IconCard icon={icon} label={label} />
              </Col>
            )
          })}
        </Row>
      </div>
    )

    const renderNotAllowedCardSection = (title, items) => (
      <div className={styles.sectionSpacing}>
        <h2 className={`${styles.subTitle} pb-m`}>{title}</h2>
        <Row gutter={[8, 8]}>
          {Array.isArray(items) ? (
            items.map((item) => {
              const { label, icon } = getNotAllowedDetails(item)
              return (
                <Col xs={24} sm={12} key={item}>
                  <IconCard icon={icon} label={label} />
                </Col>
              )
            })
          ) : (
            <Col xs={24} sm={12}>
              <IconCard icon={getNotAllowedDetails(items)?.icon} label={getNotAllowedDetails(items)?.label} />
            </Col>
          )}
        </Row>
      </div>
    )

    const renderRecommendedCardSection = (title, items) => {
      return (
        items?.length > 0 && (
          <div className={styles.sectionSpacing}>
            <h2 className={`${styles.subTitle} pb-m`}>{title}</h2>
            <Row gutter={[8, 8]}>
              {items?.map((item) => {
                const { label, icon } = getSkillLevelDetails(item)
                return (
                  <Col xs={24} sm={12} key={item}>
                    <IconCard icon={icon} label={label} />
                  </Col>
                )
              })}
            </Row>
          </div>
        )
      )
    }

    const otherItems = []
    if (accessibilityEnabled) otherItems.push("Wheelchair accessible")
    if (childrenAllowed) otherItems.push("Child friendly")
    if (alcoholAllowed) otherItems.push("Alcohol allowed")

    const renderOtherCardSection = (title, items) => (
      <div className={styles.sectionSpacing}>
        <h2 className={`${styles.subTitle} pb-m`}>{title}</h2>
        <Row gutter={[8, 8]}>
          {items.map((item) => {
            const { label, icon } = getOtherDetails(item)
            return (
              <Col xs={24} sm={12} key={item}>
                <IconCard icon={icon} label={label} />
              </Col>
            )
          })}
        </Row>
      </div>
    )

    return (
      <Card className={`${styles.mainCard} ${styles.tripDetails}`} title="Trip Details">
        {includedItems?.length > 0 && renderCardSection(t("trip.labels.included_items"), includedItems)}
        {bringItems?.length > 0 && <div className={styles.commonDetailCards}>{renderCardSection(t("trip.labels.bring_items"), bringItems)}</div>}
        {!alcoholAllowed && (
          <div className={styles.commonDetailCards}>{renderNotAllowedCardSection(t("trip.labels.not_allowed_to_bring_items"), ["alcohol"])}</div>
        )}
        {<div className={styles.commonDetailCards}>{renderRecommendedCardSection(t("Suitable for Skill Levels"), skillLevels)}</div>}
        {<div className={styles.commonDetailCards}>{otherItems.length > 0 && renderOtherCardSection(t("trip.labels.other"), otherItems)}</div>}
      </Card>
    )
  }
)

TripDetailsCard.propTypes = {
  includedItems: PropTypes.arrayOf(PropTypes.string),
  bringItems: PropTypes.arrayOf(PropTypes.string),
  skillLevels: PropTypes.arrayOf(PropTypes.string),
  alcoholAllowed: PropTypes.bool,
  accessibilityEnabled: PropTypes.bool,
  childrenAllowed: PropTypes.bool
}

TripDetailsCard.displayName = "TripDetailsCard"

export default TripDetailsCard
